import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import { Dropdown, Loader, Pagination } from 'semantic-ui-react';
import { useSelector } from 'react-redux';

import { Text, ErrorToast, Search, Button, Icon } from 'components';

import EmployeeService from 'api/employees';
import SharedProfileService from 'api/sharedProfile';
import EmployeesTable from './EmployeesTable';

import MemberInviteModal from '../../forms/MemberInvite/MemberInvite_Modal';
import CrewInviteModal from './crew-invite-modal/CrewInviteModal';
import UpdateDepartmentModal from './components/updateDepartmentModal/UpdateDepartmentModal';
import BulkInviteModal from './components/bulkInviteModal/BulkInviteModal';
import ReinviteModal from './components/reInviteModal/ReInviteModal';
import AssignClinicModal from './components/assignClinicModal/AssignClinicModal';

// New UI components (used when useNewUI prop is true)
import CrewStatusTabs from './crew-status-tabs/CrewStatusTabs';
import MemberSearchBar from './member-search-bar/MemberSearchBar';
import TablePagination from './table-pagination/TablePagination';
import ProfileDrawer from './components/profile-drawer/ProfileDrawer';
import AssignVesselModal from './components/assign-vessel-modal/AssignVesselModal';
import ContactMemberModal from './components/contact-member-modal/ContactMemberModal';
import UploadDocumentModal from './components/upload-document-modal/UploadDocumentModal';
import CrewFilterPopover from './crew-filter-popover/CrewFilterPopover';

import { computeCrewStatus, CREW_STATUS } from '../utils/compute-crew-status';

// import history from '../../../history';
import './employeesTable.scss';

const EmplyeeOverview = ({
  departments,
  objDepts,
  viewingAccount,
  hasWriteRights,
  hasViewRights,
  getDepartmentsFilter,
  useNewUI = false,
  onAddMemberReady,
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [memberInviteToggled, toggleMemberInvite] = useState(false);
  const [departmentModalToggled, toggleDepartmentModal] = useState(false);
  const [assignClinicalModalToggled, toggleAssignClinicModal] = useState(false);
  const [bulkInvToggled, toggleBulkInvModal] = useState(false);
  const [reInvToggled, toggleReInvModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [editMember, setEditMember] = useState(null);
  const [employees, setEmployees] = useState();
  const [formattedWriteDepts, setFormattedWriteDepts] = useState([]);
  const [isDeleting, setDeleting] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [activePage, setActivePage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // New UI state (Phase 3 tabs + Phase 5 modals/drawers)
  const [activeTab, setActiveTab] = useState('all');
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [profileDrawerMember, setProfileDrawerMember] = useState(null);
  const [assignVesselOpen, setAssignVesselOpen] = useState(false);
  const [assignVesselMember, setAssignVesselMember] = useState(null);
  const [contactMemberOpen, setContactMemberOpen] = useState(false);
  const [contactMemberTarget, setContactMemberTarget] = useState(null);
  const [uploadDocOpen, setUploadDocOpen] = useState(false);
  const [uploadDocMember, setUploadDocMember] = useState(null);
  const [docRefreshKey, setDocRefreshKey] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [crewFilters, setCrewFilters] = useState({ department: '', designation: '' });
  const isFirstRender = useRef(true);

  const { writeDepts } = useSelector(
    (state) => state.general?.userRightsObj?.employees || {}
  );

  useEffect(() => {
    setUrlValuesToState();
  }, []);

  // Expose invite handler to parent after component mounts (for CrewPageHeader "Add Crew Member" button)
  useEffect(() => {
    if (useNewUI && onAddMemberReady) {
      onAddMemberReady(() => toggleMemberInvite(true));
    }
  }, [useNewUI, onAddMemberReady]);

  useEffect(() => {
    fetchInitialData();
  }, [viewingAccount, activePage]);

  useEffect(() => {
    setWriteDepts();
  }, [departments]);

  useEffect(() => {
    if (!memberInviteToggled) setEditMember(null);
  }, [memberInviteToggled]);

  // Sync profile drawer member with latest data after fetches (e.g. after vessel assignment)
  useEffect(() => {
    if (profileDrawerOpen && profileDrawerMember?._id && filteredData?.length) {
      const updated = filteredData.find((e) => e._id === profileDrawerMember._id);
      if (updated) {
        setProfileDrawerMember({
          ...updated,
          departmentName: updated?.departments?.length ? objDepts[updated.departments[0]] : '',
        });
      }
    }
  }, [filteredData]);

  // Re-fetch when crew filters change (skip initial mount — fetchInitialData handles that)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setActivePage(1);
    fetchEmployees(searchValue);
  }, [crewFilters]);

  const setWriteDepts = () => {
    if (!departments) return;
    const _writeDepts = [];

    departments.forEach((dept) => {
      if (writeDepts.includes(dept._id)) _writeDepts.push(dept);
    });
    setFormattedWriteDepts(_writeDepts);
  };

  const fetchInitialData = async () => {
    const data = await fetchEmployees(searchValue);
    setEmployees(data);
  };

  const fetchEmployees = async (search) => {
    try {
      setLoadingEmps(true);
      const filters = getDepartmentsFilter();
      if (search || Object.keys(filters).length) setActivePage(1);
      if (filters === 'NA') return;
      filters.isDeleted = false;
      filters.isActive = true;
      if (crewFilters.department) {
        // Intersect: only apply if selected dept is within user's rights-based departments
        const rightsDepts = filters.departments || [];
        if (!rightsDepts.length || rightsDepts.includes(crewFilters.department)) {
          filters.departments = [crewFilters.department];
        }
      }
      if (crewFilters.designation) {
        filters.designation = [crewFilters.designation];
      }
      const _activePage = activePage || 1;
      const limit = 30;
      const offset = limit * (_activePage - 1);
      const {
        data: { data, totalRecord },
      } = await EmployeeService.getAll({
        filters,
        search,
        limit,
        offset,
      });
      setFilteredData(data);
      setTotalRecords(totalRecord || 0);
      setTotalPages(Math.ceil(totalRecord / limit) || 0);
      setLoadingEmps(false);
      return data;
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
      setLoadingEmps(false);
    }
  };

  const onRowSelect = (e, id, emp) => {
    e.stopPropagation();
    e.preventDefault();

    if (selectedRows[id]) delete selectedRows[id];
    else selectedRows[id] = emp;

    setSelectedRows({ ...selectedRows });
  };

  const deleteEmployee = async (emp) => {
    try {
      setDeleting(true);
      await EmployeeService.delete({
        id: emp._id,
        userId: emp?.patientId,
        clientId: emp?.clientId,
      });
      fetchEmployees(searchValue);
      toggleMemberInvite(false);
      setDeleting(false);
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
      setDeleting(false);
    }
  };

  const selectAllRows = (e) => {
    e.stopPropagation();
    e.preventDefault();
    let rows;

    if (Object.keys(selectedRows)?.length === filteredData?.length) {
      setSelectedRows({});
      rows = {};
    } else {
      rows = {};
      filteredData.forEach((element) => {
        rows[element._id] = element;
      });
      setSelectedRows(rows);
    }
  };

  const handleDropdownSelect = (value) => {
    if (value === 'invite') return toggleMemberInvite(true);
    if (value === 'bulk_invite') return toggleBulkInvModal(true);
    if (value === 're-invite') return toggleReInvModal(true);
    if (value === 'department') return toggleDepartmentModal(true);
    if (value === 'assignClinic') return toggleAssignClinicModal(true);
  };

  const onSearchChange = async (value) => {
    setSearchValue(value);
    handleSearch(value);
  };

  const handleSearch = useCallback(
    debounce((search) => fetchEmployees(search), 500),
    []
  );

  const setUrlValuesToState = () => {
    const params = new URLSearchParams(window.location.search);
    let page = params.get('page');
    if (!page || page < 1) page = 1;
    setActivePage(Number(page));
    updateSearchParams(page);
  };

  const updateSearchParams = (page) => {
    const newurl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname +
      `?page=${page}`;
    window.history.pushState({ path: newurl }, '', newurl);
  };

  const onEditClick = (emp) => {
    toggleMemberInvite(true);
    setEditMember(emp);
  };

  const goToPreviousPage = () => {
    if (activePage <= 1) return;
    const page = Math.max(1, activePage - 1);
    setActivePage(page);
    updateSearchParams(page);
  };

  const goToNextPage = () => {
    const page = Math.min(totalPages, activePage + 1);
    setActivePage(page);
    updateSearchParams(page);
  };
  const onPageClick = (page) => {
    updateSearchParams(page);
    setActivePage(page);
  };

  // New UI handlers (Phase 5 modals/drawers)
  const onViewProfile = (emp) => {
    const enriched = {
      ...emp,
      departmentName: emp?.departments?.length ? objDepts[emp.departments[0]] : '',
    };
    setProfileDrawerMember(enriched);
    setProfileDrawerOpen(true);
  };

  const onAssignVessel = (emp) => {
    setAssignVesselMember(emp);
    setAssignVesselOpen(true);
  };

  const onAssignClinic = (emp) => {
    setSelectedRows({ [emp._id]: emp });
    toggleAssignClinicModal(true);
  };

  const onContactMember = (emp) => {
    setContactMemberTarget(emp);
    setContactMemberOpen(true);
  };

  const onFilterChange = (key, value) => {
    setCrewFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onClearFilters = () => {
    setCrewFilters({ department: '', designation: '' });
  };

  const activeFilterCount = Object.values(crewFilters).filter(Boolean).length;

  const uniqueDesignations = [...new Set(
    (filteredData || []).map((e) => e.designation).filter(Boolean)
  )].sort();

  const onUploadDocument = (emp) => {
    setUploadDocMember(emp || profileDrawerMember);
    setUploadDocOpen(true);
  };

  // Tab filtering logic — dynamic status from signOnDate/signOffDate vs today
  const getTabFilteredData = () => {
    if (activeTab === 'all') return filteredData;
    const tabStatusMap = {
      onboard: [CREW_STATUS.ONBOARD],
      onLeave: [CREW_STATUS.ON_LEAVE, CREW_STATUS.SICK_LEAVE],
      pending: [CREW_STATUS.INVITED, CREW_STATUS.PENDING],
    };
    const allowed = tabStatusMap[activeTab] || [];
    return filteredData.filter((emp) => allowed.includes(computeCrewStatus(emp)));
  };

  const getTabCounts = () => ({
    all: filteredData?.length || 0,
    onboard: filteredData?.filter((e) => computeCrewStatus(e) === CREW_STATUS.ONBOARD).length || 0,
    onLeave: filteredData?.filter((e) => [CREW_STATUS.ON_LEAVE, CREW_STATUS.SICK_LEAVE].includes(computeCrewStatus(e))).length || 0,
    pending: filteredData?.filter((e) =>
      [CREW_STATUS.INVITED, CREW_STATUS.PENDING].includes(computeCrewStatus(e))
    ).length || 0,
  });

  // Shared modals (used by both old and new UI)
  const renderModals = () => (
    <>
      {memberInviteToggled && useNewUI ? (
        <CrewInviteModal
          open={memberInviteToggled}
          onClose={() => toggleMemberInvite(false)}
          member={editMember}
          fetchEmployees={fetchEmployees}
          departments={formattedWriteDepts}
          onDelete={deleteEmployee}
          isDeleting={isDeleting}
        />
      ) : memberInviteToggled && (
        <MemberInviteModal
          toggleModal={toggleMemberInvite}
          isModalToggled={memberInviteToggled}
          member={editMember}
          fetchEmployees={fetchEmployees}
          departments={formattedWriteDepts}
          onDelete={deleteEmployee}
          isDeleting={isDeleting}
        />
      )}
      {departmentModalToggled && (
        <UpdateDepartmentModal
          toggleModal={toggleDepartmentModal}
          isModalToggled={departmentModalToggled}
          selectedEmps={selectedRows}
          departments={formattedWriteDepts}
          fetchEmployees={fetchEmployees}
          formattedDepts={objDepts}
        />
      )}
      {assignClinicalModalToggled && (
        <AssignClinicModal
          toggleModal={toggleAssignClinicModal}
          isModalToggled={assignClinicalModalToggled}
          selectedEmps={selectedRows}
          departments={formattedWriteDepts}
          onSuccess={() => fetchEmployees(searchValue)}
        />
      )}
      {bulkInvToggled && (
        <BulkInviteModal
          toggleModal={toggleBulkInvModal}
          isModalToggled={bulkInvToggled}
          departments={formattedWriteDepts}
          fetchEmployees={fetchEmployees}
        />
      )}
      {reInvToggled && (
        <ReinviteModal
          toggleModal={toggleReInvModal}
          isModalToggled={reInvToggled}
          selectedEmps={selectedRows}
        />
      )}
    </>
  );

  // New Crew Management UI
  if (useNewUI) {
    const tabFilteredData = getTabFilteredData();

    return (
      <div>
        <div className="emp-table-wrapper">
          {renderModals()}

          {/* New modals and drawer */}
          <ProfileDrawer
            open={profileDrawerOpen}
            onClose={() => setProfileDrawerOpen(false)}
            member={profileDrawerMember}
            onUploadDocument={onUploadDocument}
            onAssignVessel={onAssignVessel}
            onEditMember={(emp) => {
              setProfileDrawerOpen(false);
              onEditClick(emp);
            }}
            refreshKey={docRefreshKey}
          />
          <AssignVesselModal
            open={assignVesselOpen}
            onClose={() => setAssignVesselOpen(false)}
            member={assignVesselMember}
            departments={formattedWriteDepts}
            fetchEmployees={() => fetchEmployees(searchValue)}
          />
          <ContactMemberModal
            open={contactMemberOpen}
            onClose={() => setContactMemberOpen(false)}
            member={contactMemberTarget}
          />
          <UploadDocumentModal
            open={uploadDocOpen}
            onClose={() => setUploadDocOpen(false)}
            member={uploadDocMember}
            onUploadSuccess={() => setDocRefreshKey((k) => k + 1)}
          />

          {!hasViewRights ? (
            <Text bold center>
              You don't have Member view rights for this department
            </Text>
          ) : (
            <>
              {!loadingEmps && !employees?.length ? (
                <div className="emp-table-wrapper__placeholder">
                  <Text bold center>Currently there are no members invited.</Text>
                  <Text bold center>Start the setup by inviting someone from the workforce.</Text>
                  <Button className="mt-3" size="wide" color="blue" onClick={() => handleDropdownSelect('invite')}>
                    Invite member now
                  </Button>
                  <Button className="mt-2" size="wide" color="blue" inverted onClick={() => handleDropdownSelect('bulk_invite')}>
                    Upload members CSV
                  </Button>
                </div>
              ) : (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm">
                  <div className="tw-px-4 tw-pt-2 tw-pb-4 tw-border-b tw-border-gray-200 tw-space-y-3">
                  <CrewStatusTabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    counts={getTabCounts()}
                  />
                  <MemberSearchBar
                    searchValue={searchValue}
                    onSearchChange={onSearchChange}
                    onFiltersClick={() => setFilterOpen(!filterOpen)}
                    filterOpen={filterOpen}
                    activeFilterCount={activeFilterCount}
                  >
                    {filterOpen && (
                      <CrewFilterPopover
                        onClose={() => setFilterOpen(false)}
                        filters={crewFilters}
                        onFilterChange={onFilterChange}
                        onClearFilters={onClearFilters}
                        departments={departments}
                        designations={uniqueDesignations}
                      />
                    )}
                  </MemberSearchBar>
                  </div>
                  <div>
                    {loadingEmps ? (
                      <div className="mt-5">
                        <Loader active inline="centered" />
                      </div>
                    ) : (
                      <EmployeesTable
                        hasEditRights={hasWriteRights}
                        formattedDepts={objDepts}
                        filteredData={tabFilteredData}
                        selectedRows={selectedRows}
                        onRowSelect={onRowSelect}
                        selectAllRows={selectAllRows}
                        onEditClick={onEditClick}
                        useNewUI={true}
                        onViewProfile={onViewProfile}
                        onAssignVessel={onAssignVessel}
                        onAssignClinic={onAssignClinic}
                        onContactMember={onContactMember}
                        onDeleteMember={deleteEmployee}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {!loadingEmps && parseInt(totalPages) > 0 && filteredData.length > 0 && (
          <TablePagination
            activePage={activePage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            pageSize={30}
            onPageClick={onPageClick}
          />
        )}
      </div>
    );
  }

  // Legacy UI (default)
  return (
    <div>
      <div className="emp-table-wrapper">
        {renderModals()}
        {!hasViewRights ? (
          <Text bold center>
            You don't have Member view rights for this department
          </Text>
        ) : (
          <>
            {!loadingEmps && !employees?.length ? (
              <div className="emp-table-wrapper__placeholder">
                <Text bold center>
                  Currently there are no members invited.
                </Text>
                <Text bold center>
                  Start the setup by inviting someone from the workforce.
                </Text>
                <Button
                  className="mt-3"
                  size="wide"
                  color="blue"
                  onClick={() => handleDropdownSelect('invite')}
                >
                  Invite member now
                </Button>
                <Button
                  className="mt-2"
                  size="wide"
                  color="blue"
                  inverted
                  onClick={() => handleDropdownSelect('bulk_invite')}
                >
                  Upload members CSV
                </Button>
              </div>
            ) : (
              <>
                <div className="emp-table-wrapper__search">
                  <Search
                    isLoading={isSearching}
                    size="fullWidth"
                    border
                    value={searchValue}
                    dropdown={false}
                    onSearchChange={(e, { value }) => onSearchChange(value)}
                    isFilter={true}
                    placeholder="Search"
                  />
                  {hasWriteRights && (
                    <Dropdown
                      className="button icon emp-table-wrapper__dropdown"
                      onChange={handleDropdownSelect}
                      selectOnBlur={false}
                      floating
                      direction="left"
                      trigger={
                        <Text size="small" color="white">
                          Actions
                        </Text>
                      }
                    >
                      <Dropdown.Menu>
                        <Dropdown.Header content="Department" />
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => handleDropdownSelect('department')}
                          disabled={!Object.keys(selectedRows)?.length}
                        >
                          Update department
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleDropdownSelect('assignClinic')}
                          disabled={!Object.keys(selectedRows)?.length}
                        >
                          Assign medical clinic
                        </Dropdown.Item>
                        <Dropdown.Header content="Invitation" />
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => handleDropdownSelect('invite')}
                        >
                          Invite new member
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleDropdownSelect('bulk_invite')}
                        >
                          Bulk invite members
                        </Dropdown.Item>
                        <Dropdown.Item
                          disabled={!Object.keys(selectedRows)?.length}
                          onClick={() => handleDropdownSelect('re-invite')}
                        >
                          Re-invite member(s)
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>
                <div className="mt-3">
                  {loadingEmps ? (
                    <div className="mt-5">
                      <Loader active inline="centered" />
                    </div>
                  ) : (
                    <EmployeesTable
                      hasEditRights={hasWriteRights}
                      formattedDepts={objDepts}
                      filteredData={filteredData}
                      selectedRows={selectedRows}
                      onRowSelect={onRowSelect}
                      selectAllRows={selectAllRows}
                      onEditClick={onEditClick}
                    />
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
      {!loadingEmps && parseInt(totalPages) > 0 && filteredData.length > 0 && (
        <div className="emp-table-wrapper__pagination">
          <Text color="grey">{'Page ' + activePage + ' of ' + totalPages}</Text>
          {parseInt(totalPages) > 1 && (
            <div className="emp-table-wrapper__pagination__section">
              <div className="emp-table-wrapper__pagination__section__angles">
                <Pagination
                  activePage={activePage}
                  firstItem={null}
                  lastItem={null}
                  pointing
                  secondary
                  totalPages={totalPages}
                  onPageChange={(e, { activePage }) => onPageClick(activePage)}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default withRouter(EmplyeeOverview);
